/**
 * OkReserva — Configuración de Planes de Suscripción
 * Define los 4 planes y qué secciones habilita cada uno.
 */

export const PLAN_IDS = {
  FREE:  'gratuito',
  BASIC: 'basico',
  PRO:   'profesional',
  ENT:   'empresarial',
};

export const PLANS = [
  {
    id: PLAN_IDS.FREE,
    name: 'Gratuito',
    tagline: 'Para empezar sin riesgo',
    price_mxn: 0,
    price_usd: 0,
    stripe_price_id: null,
    badge: null,
    color: 'from-gray-400 to-gray-600',
    cardBg: 'bg-gray-50 border-gray-200',
    btnClass: 'bg-gray-700 hover:bg-gray-800 text-white',
    limits: {
      reservations_per_month: 50,
      users: 1,
      locations: 1,
    },
    features: [
      'Hasta 50 reservaciones al mes',
      'Libro digital básico',
      'Lista de espera',
      'Página de reservas pública',
      'CRM de clientes (básico)',
      '1 usuario administrador',
    ],
  },
  {
    id: PLAN_IDS.BASIC,
    name: 'Básico',
    tagline: 'Para restaurantes en crecimiento',
    price_mxn: 299,
    price_usd: 15,
    stripe_price_id: 'price_basic_monthly',
    badge: null,
    color: 'from-blue-500 to-blue-700',
    cardBg: 'bg-blue-50 border-blue-200',
    btnClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    limits: {
      reservations_per_month: -1, // unlimited
      users: 3,
      locations: 1,
    },
    features: [
      'Reservaciones ilimitadas',
      'Plano de sala interactivo',
      'Depósitos y prepagos',
      'WhatsApp automático',
      'Programa de fidelización',
      'Tarjetas regalo digitales',
      'Fila digital walk-in',
      'Analytics básico',
      'Hasta 3 usuarios',
    ],
  },
  {
    id: PLAN_IDS.PRO,
    name: 'Profesional',
    tagline: 'El favorito de los restaurantes exitosos',
    price_mxn: 699,
    price_usd: 35,
    stripe_price_id: 'price_pro_monthly',
    badge: 'Más popular',
    color: 'from-orange-500 to-orange-700',
    cardBg: 'bg-orange-50 border-orange-500',
    btnClass: 'bg-orange-500 hover:bg-orange-600 text-white',
    limits: {
      reservations_per_month: -1,
      users: 8,
      locations: 2,
    },
    features: [
      'Todo lo del plan Básico',
      'Eventos y venta de tickets',
      'Comedores privados',
      'Menú interactivo con alérgenos',
      'Facturación SAT / CFDI 4.0',
      'Campañas de marketing',
      'Seguimiento post-visita',
      'Feedback & NPS automático',
      'Predicción de no-shows (IA)',
      'Benchmark de mercado anónimo',
      'Membresías de restaurante',
      'Hasta 8 usuarios y 2 sucursales',
    ],
  },
  {
    id: PLAN_IDS.ENT,
    name: 'Empresarial',
    tagline: 'Para grupos y cadenas de restaurantes',
    price_mxn: 1499,
    price_usd: 75,
    stripe_price_id: 'price_ent_monthly',
    badge: 'Máximo poder',
    color: 'from-gray-800 to-black',
    cardBg: 'bg-gray-900 border-gray-700',
    btnClass: 'bg-gray-900 hover:bg-black text-white border border-gray-700',
    limits: {
      reservations_per_month: -1,
      users: -1, // unlimited
      locations: -1,
    },
    features: [
      'Todo lo del plan Profesional',
      'Portal de grupos corporativos',
      'Programación de personal y turnos',
      'Membresías Acceso Garantizado (VIP)',
      'Integraciones avanzadas (Kapso + SendPulse)',
      'Usuarios y sucursales ilimitados',
      'API access completo',
      'Soporte prioritario 24/7',
      'Manager de cuenta dedicado',
    ],
  },
];

/**
 * Mapa de sección → plan mínimo requerido
 * Si la sección no está en este mapa, es libre para todos los planes.
 */
export const SECTION_PLAN_MAP = {
  // FREE — disponible para todos
  dashboard:          PLAN_IDS.FREE,
  libro:              PLAN_IDS.FREE,
  reservations:       PLAN_IDS.FREE,
  waitlist:           PLAN_IDS.FREE,
  customers:          PLAN_IDS.FREE,
  'booking-page':     PLAN_IDS.FREE,

  // BÁSICO
  'floor-plan':       PLAN_IDS.BASIC,
  loyalty:            PLAN_IDS.BASIC,
  deposits:           PLAN_IDS.BASIC,
  'gift-cards':       PLAN_IDS.BASIC,
  whatsapp:           PLAN_IDS.BASIC,
  analytics:          PLAN_IDS.BASIC,
  'walk-in':          PLAN_IDS.BASIC,

  // PROFESIONAL
  events:             PLAN_IDS.PRO,
  'allergen-menu':    PLAN_IDS.PRO,
  allergens:          PLAN_IDS.PRO,
  'private-dining':   PLAN_IDS.PRO,
  cfdi:               PLAN_IDS.PRO,
  nps:                PLAN_IDS.PRO,
  marketing:          PLAN_IDS.PRO,
  'risk-dashboard':   PLAN_IDS.PRO,
  followup:           PLAN_IDS.PRO,
  benchmark:          PLAN_IDS.PRO,
  memberships:        PLAN_IDS.PRO,
  experiences:        PLAN_IDS.PRO,

  // EMPRESARIAL
  corporate:          PLAN_IDS.ENT,
  personal:           PLAN_IDS.ENT,
  'acceso-garantizado': PLAN_IDS.ENT,
  integrations:       PLAN_IDS.ENT,
};

/** Orden de planes para comparar */
const PLAN_ORDER = [PLAN_IDS.FREE, PLAN_IDS.BASIC, PLAN_IDS.PRO, PLAN_IDS.ENT];

/** ¿El plan `userPlan` tiene acceso a la sección `section`? */
export function canAccess(section, userPlan = PLAN_IDS.FREE) {
  const required = SECTION_PLAN_MAP[section] || PLAN_IDS.FREE;
  return PLAN_ORDER.indexOf(userPlan) >= PLAN_ORDER.indexOf(required);
}

/** Devuelve el objeto plan por id */
export function getPlan(planId) {
  return PLANS.find(p => p.id === planId) || PLANS[0];
}

/** Devuelve el plan mínimo requerido para una sección */
export function getRequiredPlan(section) {
  const planId = SECTION_PLAN_MAP[section] || PLAN_IDS.FREE;
  return getPlan(planId);
}
